import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import "./AppNav.css";

const NOTIFICATION_POLL_INTERVAL_MS = 8000;

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: "home" },
  { to: "/library", label: "Library", icon: "book_4" },
  { to: "/notifications", label: "Notifications", icon: "notifications_active" },
  { to: "/profile", label: "Profile", icon: "person" }
];

function AppNav() {
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [isDesktopNavOpen, setIsDesktopNavOpen] = useState(() => {
    try {
      return localStorage.getItem("app-nav-desktop-open") === "1";
    } catch (error) {
      return false;
    }
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 501px)");
    const handleChange = (event) => {
      if (!event.matches) {
        setIsDesktopNavOpen(false);
        try {
          localStorage.removeItem("app-nav-desktop-open");
        } catch (error) {}
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    try {
      if (isDesktopNavOpen) {
        localStorage.setItem("app-nav-desktop-open", "1");
      } else {
        localStorage.removeItem("app-nav-desktop-open");
      }
    } catch (error) {}
  }, [isDesktopNavOpen]);

  useEffect(() => {
    const fetchNotificationStatus = async () => {
      const apiUrl = process.env.REACT_APP_API_URL;
      const userId = JSON.parse(localStorage.getItem("user") || "{}").id || 0;

      if (!apiUrl || !userId) {
        setHasUnreadNotifications(false);
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/get-notification-status.php?uid=${userId}`, {
          credentials: "include",
          cache: "no-store"
        });
        const data = await response.json();
        if (data?.success) {
          setHasUnreadNotifications(Boolean(data.has_unread));
        }
      } catch (error) {
        console.error("Failed to load notification status:", error);
      }
    };

    fetchNotificationStatus();

    const handleRefresh = (event) => {
      const eventHasUnread = event?.detail?.hasUnread;
      if (typeof eventHasUnread === "boolean") {
        setHasUnreadNotifications(eventHasUnread);
        return;
      }
      fetchNotificationStatus();
    };

    window.addEventListener("notifications-updated", handleRefresh);
    window.addEventListener("focus", handleRefresh);
    window.addEventListener("storage", handleRefresh);
    window.addEventListener("profile-updated", handleRefresh);
    const interval = window.setInterval(handleRefresh, NOTIFICATION_POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener("notifications-updated", handleRefresh);
      window.removeEventListener("focus", handleRefresh);
      window.removeEventListener("storage", handleRefresh);
      window.removeEventListener("profile-updated", handleRefresh);
      window.clearInterval(interval);
    };
  }, []);

  const renderNavIcon = (item) => (
    <span className="app-nav__icon-wrap">
      <span className="material-symbols-rounded app-nav__icon">
        {item.icon}
      </span>
      {item.to === "/notifications" && hasUnreadNotifications && (
        <span className="app-nav__notif-dot" aria-hidden="true" />
      )}
    </span>
  );

  return (
    <>
      <nav className="app-nav app-nav--mobile" aria-label="Primary">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            aria-label={item.label}
            className={({ isActive }) =>
              `app-nav__link${isActive ? " app-nav__link--active" : ""}`
            }
          >
            {renderNavIcon(item)}
          </NavLink>
        ))}
      </nav>

      <div className="app-nav-desktop">
        {isDesktopNavOpen && (
          <button
            type="button"
            className="app-nav-desktop__backdrop"
            aria-label="Close navigation"
            onClick={() => setIsDesktopNavOpen(false)}
          />
        )}

        <div className="app-nav-desktop__wrap">
          <div
            className={`app-nav-desktop__bar${isDesktopNavOpen ? " app-nav-desktop__bar--open" : ""}`}
            aria-hidden={!isDesktopNavOpen}
          >
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                aria-label={item.label}
                className={({ isActive }) =>
                  `app-nav__link app-nav-desktop__link${isActive ? " app-nav__link--active" : ""}`
                }
              >
                {renderNavIcon(item)}
              </NavLink>
            ))}
          </div>

          <button
            type="button"
            className={`app-nav-desktop__toggle${isDesktopNavOpen ? " app-nav-desktop__toggle--open" : ""}`}
            aria-label="Open navigation"
            aria-expanded={isDesktopNavOpen}
            onClick={() => setIsDesktopNavOpen((open) => !open)}
          >
            <span className="material-symbols-rounded">menu_book</span>
          </button>
        </div>
      </div>
    </>
  );
}

export default AppNav;
